/* eslint-disable react/no-unknown-property -- react-three-fiber maps three.js
   properties (position, intensity, args, attach, object, …) onto intrinsic
   JSX elements; these are valid r3f props, not DOM attributes. */
import { Component, memo, Suspense, useLayoutEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, CircularProgress, Typography } from '@mui/material'
import { OrbitControls, useGLTF } from '@react-three/drei'
import { Canvas, useThree } from '@react-three/fiber'
import PropTypes from 'prop-types'
import * as THREE from 'three'

// SMRU/Analysis Situs GLBs carry no materials, so three.js falls back to a
// metalness=1 default that renders black without an environment map. Override
// every mesh with a neutral, non-metallic CAD-grey so the part is always visible.
const PART_MATERIAL = new THREE.MeshStandardMaterial({
    color: 0xb4b4b4,
    metalness: 0.15,
    roughness: 0.55,
    side: THREE.DoubleSide,
})

const classes = {
    wrapper: (height) => ({
        position: 'relative',
        width: '100%',
        height,
        borderRadius: 1,
        overflow: 'hidden',
    }),
    overlay: {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
    },
    fallbackImg: {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
    },
}

function orientObject(object) {
    if (!object) return
    try {
        object.updateMatrixWorld(true)
        const box = new THREE.Box3().setFromObject(object)
        if (box.isEmpty()) return
        const size = box.getSize(new THREE.Vector3())

        const axes = [
            { unit: new THREE.Vector3(1, 0, 0), len: size.x },
            { unit: new THREE.Vector3(0, 1, 0), len: size.y },
            { unit: new THREE.Vector3(0, 0, 1), len: size.z },
        ].sort((a, b) => b.len - a.len)

        // Rotate the longest axis onto horizontal X. Identity when it is already X.
        const q = new THREE.Quaternion().setFromUnitVectors(axes[0].unit, new THREE.Vector3(1, 0, 0))
        object.quaternion.premultiply(q)
        object.updateMatrixWorld(true)
    } catch {
        // Orientation is best-effort; the user can still orbit if it fails.
    }
}

function frameObject(object, camera, controls) {
    if (!object || !camera) return
    try {
        const box = new THREE.Box3().setFromObject(object)
        if (box.isEmpty()) return
        const size = box.getSize(new THREE.Vector3())
        const center = box.getCenter(new THREE.Vector3())

        const axes = [
            { unit: new THREE.Vector3(1, 0, 0), len: size.x },
            { unit: new THREE.Vector3(0, 1, 0), len: size.y },
            { unit: new THREE.Vector3(0, 0, 1), len: size.z },
        ].sort((a, b) => b.len - a.len)

        // Mostly along the two shorter axes (perpendicular to the longest one),
        // with a small component along the longest axis for a 3/4 look.
        const dir = axes[1].unit
            .clone()
            .addScaledVector(axes[2].unit, 1)
            .addScaledVector(axes[0].unit, 0.35)
            .normalize()

        // Distance that fits the bounding sphere within the vertical field of view.
        const radius = size.length() / 2 || 1
        const fov = (camera.fov * Math.PI) / 180
        const dist = (radius / Math.sin(fov / 2)) * 1.1

        camera.position.copy(center).addScaledVector(dir, dist)
        camera.near = Math.max(radius / 100, 0.01)
        camera.far = radius * 100
        camera.updateProjectionMatrix()

        if (controls) {
            controls.target.copy(center)
            controls.update()
        }
    } catch {
        // A framing failure must never break rendering; the user can still orbit.
    }
}

// The GLB scene loaded by drei's useGLTF. Suspends while the file downloads/parses
// and throws on failure, so it must sit inside a <Suspense> and an error boundary.
// On load it recolours the meshes and frames the camera broadside to the part.
const Model = ({ uri }) => {
    const { scene } = useGLTF(uri)
    const camera = useThree((state) => state.camera)
    const controls = useThree((state) => state.controls)

    useLayoutEffect(() => {
        scene.traverse((obj) => {
            if (obj.isMesh) obj.material = PART_MATERIAL
        })
        orientObject(scene)
        frameObject(scene, camera, controls)
    }, [scene, camera, controls])

    return <primitive object={scene} />
}

Model.propTypes = {
    uri: PropTypes.string.isRequired,
}

// three.js/WebGL failures (bad GLB, lost context) throw during render; a plain React
// error boundary keeps them from taking down the surrounding quote view.
class ViewerErrorBoundary extends Component {
    state = { hasError: false }

    static getDerivedStateFromError() {
        return { hasError: true }
    }

    // Marks the error as handled so it surfaces as a normal boundary fallback
    // rather than an uncaught error, and gives us a hook for logging.
    componentDidCatch() {}

    render() {
        const { hasError } = this.state
        const { children, fallback } = this.props

        if (hasError) return fallback
        return children
    }
}

ViewerErrorBoundary.propTypes = {
    children: PropTypes.node,
    fallback: PropTypes.node,
}

const GlbViewer = ({ backgroundColor = '#2b2b2b', fallbackImageUri = '', height = 400, uri = '' }) => {
    const { t } = useTranslation()

    const message = (content) => <Box sx={classes.overlay}>{content}</Box>

    // When there is no 3D model (e.g. SMRU could not produce a GLB) or it fails to
    // load, show the static preview image if we have one so the CAD view still
    // shows the part; otherwise show an explanatory message.
    const fallbackContent = (altText) =>
        fallbackImageUri ? (
            <Box
                alt={altText}
                component="img"
                src={fallbackImageUri}
                sx={classes.fallbackImg}
            />
        ) : (
            message(<Typography color="text.secondary">{altText}</Typography>)
        )

    if (!uri) {
        return <Box sx={classes.wrapper(height)}>{fallbackContent(t('No 3D model available'))}</Box>
    }

    return (
        <Box
            data-testid="glb-viewer"
            sx={classes.wrapper(height)}
        >
            <ViewerErrorBoundary fallback={fallbackContent(t('Unable to load 3D model'))}>
                <Suspense fallback={message(<CircularProgress />)}>
                    <Canvas
                        camera={{ fov: 50, position: [1, 1, 1] }}
                        dpr={[1, 2]}
                    >
                        <color
                            args={[backgroundColor]}
                            attach="background"
                        />
                        <ambientLight intensity={0.6} />
                        <directionalLight
                            intensity={1}
                            position={[5, 5, 5]}
                        />
                        <directionalLight
                            intensity={0.3}
                            position={[-5, -5, -5]}
                        />
                        {/* Model frames the camera itself (broadside to the part's longest
                            axis) once loaded — see frameObject. */}
                        <Model uri={uri} />
                        <OrbitControls
                            enablePan
                            enableRotate
                            enableZoom
                            makeDefault
                        />
                    </Canvas>
                </Suspense>
            </ViewerErrorBoundary>
        </Box>
    )
}

GlbViewer.propTypes = {
    backgroundColor: PropTypes.string,
    fallbackImageUri: PropTypes.string,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    uri: PropTypes.string,
}

export default memo(GlbViewer)
